import { ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import { api } from '@/boot/axios'
import * as Sentry from '@sentry/vue'
import { backends } from '@/assets/backends'
import { showSuccess } from '@/utils/ui'
import { handleError } from '@/utils/errors'

interface Account {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  is_finance: boolean
}

export const useAccountStore = defineStore('account', () => {
  const router = useRouter()

  const backend = ref(backends.find((b) => b.hostname === window.location.hostname))
  const account = ref<Account | null>(null)
  watch(account, (newVal) => {
    if (newVal) {
      showSuccess(`You are logged in as ${newVal.email}`)
    }
  })

  const isLoggingOut = ref(false)

  async function getCsrfToken() {
    try {
      await api.get('/../auth/oidc/csrf')
    } catch (error: any) {
      throw handleError(error, 'Failed getting CSRF token')
    }
  }

  async function getMe() {
    return api.get('/account').then((res) => {
      account.value = res.data
      Sentry.setUser({
        id: account.value!.id,
        email: account.value!.email,
      })
    })
  }

  async function logout() {
    if (isLoggingOut.value) return
    try {
      isLoggingOut.value = true
      account.value = null
      Sentry.setUser(null)
      const next = window.location.origin + '/#/login'
      const response = await api.post('/../auth/oidc/logout?next=' + next)
      if (response.data.logout_url) {
        window.location.href = response.data.logout_url
      }
    } catch (error: any) {
      console.warn(`Failed logging out: ${error.message}`)
    } finally {
      isLoggingOut.value = false
      router.push('/login')
    }
  }

  return {
    backend,
    account,
    logout,
    getMe,
    getCsrfToken,
  }
})
